import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Eye, Edit2, Save, X, ExternalLink, Mail, FileSignature, ClipboardList, Users,
  ChevronRight, ChevronLeft, Image as ImageIcon, Palette, Settings, RefreshCw, Upload, Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple Image Upload Component
function SimpleImageUpload({ label, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const token = localStorage.getItem("session_token");
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/cms/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        onChange(data.url);
        toast.success("Image uploaded!");
      } else {
        throw new Error("Upload failed");
      }
    } catch (err) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2">
        {value ? (
          <div className="relative w-16 h-16 rounded border overflow-hidden bg-gray-50 shrink-0">
            <img src={value} alt={label} className="w-full h-full object-cover" />
            <button
              onClick={() => onChange("")}
              className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        ) : (
          <div
            onClick={() => fileInputRef.current?.click()}
            className="w-16 h-16 rounded border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors"
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />
            ) : (
              <>
                <Upload className="w-4 h-4 text-gray-400" />
                <span className="text-[9px] text-gray-400">Upload</span>
              </>
            )}
          </div>
        )}
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
        {value && (
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="h-8 text-xs">
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Change"}
          </Button>
        )}
      </div>
    </div>
  );
}

// Preview frame component
function PreviewFrame({ url, title }) {
  return (
    <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
      <div className="bg-gray-100 px-3 py-2 flex items-center justify-between border-b">
        <span className="text-xs font-medium text-gray-600">{title}</span>
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
          Open <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      <iframe 
        src={url} 
        className="w-full h-[500px] border-0"
        title={title}
      />
    </div>
  );
}

// Template Editor Component
function TemplateEditor({ template, onSave, categoryColors, category }) {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState(template || {});
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formData);
      setEditing(false);
      toast.success("Template saved to ALL invites");
    } catch (err) {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Master Template
          </CardTitle>
          {!editing ? (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Edit2 className="w-4 h-4 mr-1" />Edit Template
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className={categoryColors.bg}>
                {saving ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save to All
              </Button>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">Changes here apply to ALL future and existing invites using this template</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {editing ? (
          <>
            <div>
              <Label className="text-xs">Pilot Name</Label>
              <Input value={formData.pilot_name || ""} onChange={e => setFormData(p => ({ ...p, pilot_name: e.target.value }))} placeholder={`${category} Pilot`} />
            </div>
            <div>
              <Label className="text-xs">Welcome Message</Label>
              <Textarea value={formData.welcome_message || ""} onChange={e => setFormData(p => ({ ...p, welcome_message: e.target.value }))} placeholder="You've been invited to join our pilot program..." rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <SimpleImageUpload
                label="Default Cover Image"
                value={formData.default_cover_image || ""}
                onChange={(url) => setFormData(p => ({ ...p, default_cover_image: url }))}
              />
              <SimpleImageUpload
                label="Default Background Image"
                value={formData.default_background_image || ""}
                onChange={(url) => setFormData(p => ({ ...p, default_background_image: url }))}
              />
            </div>
            <div>
              <Label className="text-xs">Default Demo Tabs</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {["makeup", "hair", "nails", "events", "wellness"].map(demo => {
                  const isSelected = (formData.default_demo_tabs || [category.toLowerCase()]).includes(demo);
                  return (
                    <button
                      key={demo}
                      type="button"
                      onClick={() => {
                        const current = formData.default_demo_tabs || [category.toLowerCase()];
                        const updated = isSelected ? current.filter(d => d !== demo) : [...current, demo];
                        setFormData(p => ({ ...p, default_demo_tabs: updated.length > 0 ? updated : [category.toLowerCase()] }));
                      }}
                      className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${isSelected ? `${categoryColors.bg} text-white` : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                    >
                      {demo.charAt(0).toUpperCase() + demo.slice(1)}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Pilot Name</span>
              <span className="font-medium">{formData.pilot_name || `${category} Pilot`}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Default Cover</span>
              <span className="font-medium truncate max-w-[200px]">{formData.default_cover_image || "None set"}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Demo Tabs</span>
              <div className="flex gap-1">
                {(formData.default_demo_tabs || [category.toLowerCase()]).map(d => (
                  <Badge key={d} variant="secondary" className="text-xs">{d}</Badge>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Individual Invite Editor Component
function IndividualEditor({ person, onSave, categoryColors, category }) {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: person?.name || person?.user_name || "",
    email: person?.email || person?.user_email || "",
    cover_image: person?.cover_image || "",
    background_image: person?.background_image || "",
    demo_tabs: person?.demo_tabs || [category.toLowerCase()],
    custom_message: person?.custom_message || ""
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(person.invite_id || person.member_id, formData);
      setEditing(false);
      toast.success("Saved for this person only");
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const isPending = person?.type === "invite";
  const stages = [
    { id: "invite", label: "Invite Page", icon: Mail, url: `/portal/invite/${person?.magic_token}` },
    { id: "nda", label: "NDA Page", icon: FileSignature, url: person?.portal_id ? `/portal/${person.portal_id}/nda` : null },
    { id: "intake", label: "Intake Form", icon: ClipboardList, url: person?.portal_id ? `/portal/${person.portal_id}/intake` : null },
    { id: "portal", label: "Portal", icon: Users, url: person?.portal_id ? `/portal/${person.portal_id}` : null }
  ];

  return (
    <Card className="border-2 border-dashed">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full ${isPending ? "bg-amber-500" : categoryColors.bg} text-white flex items-center justify-center font-medium`}>
              {(formData.name || "U").charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="font-semibold">{formData.name || "Unknown"}</h3>
              <p className="text-xs text-gray-500">{formData.email}</p>
            </div>
          </div>
          {!editing ? (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Edit2 className="w-4 h-4 mr-1" />Edit This Person
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className={categoryColors.bg}>
                {saving ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
            </div>
          )}
        </div>
        <Badge className={isPending ? "bg-amber-500 mt-2" : "bg-green-500 mt-2"}>{isPending ? "Pending" : "Active"}</Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        {editing ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Name</Label>
                <Input value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <SimpleImageUpload
                label="Cover Image (Override)"
                value={formData.cover_image || ""}
                onChange={(url) => setFormData(p => ({ ...p, cover_image: url }))}
              />
              <SimpleImageUpload
                label="Background Image (Override)"
                value={formData.background_image || ""}
                onChange={(url) => setFormData(p => ({ ...p, background_image: url }))}
              />
            </div>
            <div>
              <Label className="text-xs">Custom Message (Only for this person)</Label>
              <Textarea value={formData.custom_message} onChange={e => setFormData(p => ({ ...p, custom_message: e.target.value }))} placeholder="Personal note for this participant..." rows={2} />
            </div>
            <div>
              <Label className="text-xs">Demo Tabs (Override)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {["makeup", "hair", "nails", "events", "wellness"].map(demo => {
                  const isSelected = formData.demo_tabs.includes(demo);
                  return (
                    <button
                      key={demo}
                      type="button"
                      onClick={() => {
                        const updated = isSelected ? formData.demo_tabs.filter(d => d !== demo) : [...formData.demo_tabs, demo];
                        setFormData(p => ({ ...p, demo_tabs: updated.length > 0 ? updated : [category.toLowerCase()] }));
                      }}
                      className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${isSelected ? `${categoryColors.bg} text-white` : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                    >
                      {demo.charAt(0).toUpperCase() + demo.slice(1)}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Journey Stages */}
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">View Journey Steps:</p>
              <div className="flex flex-wrap gap-2">
                {stages.map(stage => {
                  const Icon = stage.icon;
                  const url = stage.url ? `${window.location.origin}${stage.url}` : null;
                  return (
                    <Button
                      key={stage.id}
                      variant="outline"
                      size="sm"
                      onClick={() => url ? window.open(url, "_blank") : toast.info("Not available yet")}
                      disabled={!url}
                      className="gap-1"
                      data-testid={`view-${stage.id}-btn`}
                    >
                      <Icon className="w-3 h-3" />
                      {stage.label}
                      {url && <ExternalLink className="w-3 h-3" />}
                    </Button>
                  );
                })}
              </div>
            </div>
            {/* Current settings */}
            <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Cover Image</span>
                <span className="font-medium truncate max-w-[150px]">{formData.cover_image || "Using template"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Demo Tabs</span>
                <div className="flex gap-1">{formData.demo_tabs.map(d => <Badge key={d} variant="secondary" className="text-xs">{d}</Badge>)}</div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Main Admin E2E Viewer Component
export default function AdminE2EViewer({ isOpen, onClose, people, category, categoryColors, subCategoryId, onRefresh }) {
  const [activeTab, setActiveTab] = useState("template");
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [template, setTemplate] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && subCategoryId) {
      fetchTemplate();
    }
  }, [isOpen, subCategoryId]);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/pilot-templates/${subCategoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplate(data.template || {});
      }
    } catch (err) {
      console.error("Failed to load template:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async (templateData) => {
    const token = localStorage.getItem("session_token");
    const res = await fetch(`${API}/admin/pilot-templates/${subCategoryId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(templateData)
    });
    if (!res.ok) throw new Error("Failed to save");
    setTemplate(templateData);
    onRefresh?.();
  };

  const handleSaveIndividual = async (personId, data) => {
    const token = localStorage.getItem("session_token");
    const res = await fetch(`${API}/admin/portal-invites/${personId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error("Failed to save");
    onRefresh?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[90vh] overflow-hidden flex flex-col p-0">
        <div className="px-6 pt-5 pb-3 border-b shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Admin E2E Page Viewer
              </h2>
              <p className="text-sm text-gray-500 mt-1">View and edit invite pages exactly as participants see them</p>
            </div>
            <Badge className={categoryColors.bg}>{category} Pilot</Badge>
          </div>
          
          {/* Tab Navigation */}
          <div className="flex gap-1 mt-4">
            <button
              onClick={() => setActiveTab("template")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "template" ? `${categoryColors.text} border-current` : "text-gray-500 border-transparent hover:text-gray-700"}`}
            >
              <Settings className="w-4 h-4 inline mr-1" />
              Template (All)
            </button>
            <button
              onClick={() => setActiveTab("individual")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "individual" ? `${categoryColors.text} border-current` : "text-gray-500 border-transparent hover:text-gray-700"}`}
            >
              <Users className="w-4 h-4 inline mr-1" />
              Individual ({people?.length || 0})
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : activeTab === "template" ? (
            <div className="space-y-4">
              <TemplateEditor 
                template={template} 
                onSave={handleSaveTemplate} 
                categoryColors={categoryColors}
                category={category}
              />
              
              {/* Template Preview */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Template Preview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-500 mb-3">This is how the invite page looks with default template settings</p>
                  <div className="border rounded-lg overflow-hidden bg-gray-50 p-4 text-center">
                    <p className="text-gray-500">Preview loads when you open an invite link</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={() => {
                        const demoUrl = `${window.location.origin}/portal/invite/demo_preview_${subCategoryId}`;
                        window.open(demoUrl, "_blank");
                      }}
                    >
                      Open Template Preview
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">Edit settings for individual participants. Changes only affect this person.</p>
              {people?.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500">No participants yet</p>
                  </CardContent>
                </Card>
              ) : (
                people?.map(person => (
                  <IndividualEditor
                    key={person.invite_id || person.member_id}
                    person={person}
                    onSave={handleSaveIndividual}
                    categoryColors={categoryColors}
                    category={category}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
